// src/config/theme.js
import { createTheme, rem } from '@mantine/core';

// Modular Type Scale (1.250 - Major Third)
const typeScale = {
	xs: rem(12),    // 0.75rem
	sm: rem(14),    // 0.875rem
	base: rem(16),  // 1rem
	md: rem(18),    // 1.125rem
	lg: rem(20),    // 1.25rem
	xl: rem(24),    // 1.5rem
	'2xl': rem(30), // 1.875rem
	'3xl': rem(36), // 2.25rem
	'4xl': rem(48), // 3rem
	'5xl': rem(60), // 3.75rem
	'6xl': rem(72)  // 4.5rem
};

export const theme = createTheme({
	// Primary colors (set to brand accent inspired by logo)
	primaryColor: 'accent',
	colors: {
		// Deep navy base for backgrounds
		navy: [
			'#E7E9EF', '#C5CAD9', '#9BA3BC', '#717C9F', '#4B5682', '#2B3665', '#232D59', '#1B244C', '#141B3F', '#0D1232'
		],
		// Brand accent (logo orange) with extended scale
		accent: [
			'#FFF5ED', // 0
			'#FFE6D1', // 1
			'#FFD1AB', // 2
			'#FFB97D', // 3
			'#FF9F4D', // 4
			'#FF881F', // 5 primary accent (logo-like)
			'#F26E00', // 6
			'#D85F00', // 7
			'#BF5300', // 8
			'#A24600'  // 9
		],
		// Supporting gradient partner (warm amber)
		amber: ['#FFF8E7','#FFECC2','#FFDFA0','#FFD07A','#FFC055','#FFAE2E','#F29900','#DA8800','#BF7700','#A36500']
	},

	// Font settings
	fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif',
	fontFamilyMonospace: '"JetBrains Mono", "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", monospace',
	
	// Base font sizes
	fontSizes: {
		xs: typeScale.xs,
		sm: typeScale.sm,
		md: typeScale.base,
		lg: typeScale.lg,
		xl: typeScale.xl
	},
	
	// Line heights for better readability
	lineHeights: {
		xs: 1.4,
		sm: 1.5,
		md: 1.6,
		lg: 1.65,
		xl: 1.75
	},

	// Heading styles with improved scale
	headings: {
		fontFamily: '"Plus Jakarta Sans", sans-serif',
		fontWeight: '600',
		sizes: {
			h1: { fontSize: typeScale['4xl'], lineHeight: 1.2, fontWeight: '700', letterSpacing: '-0.025em' },
			h2: { fontSize: typeScale['3xl'], lineHeight: 1.25, fontWeight: '600', letterSpacing: '-0.02em' },
			h3: { fontSize: typeScale['2xl'], lineHeight: 1.3, fontWeight: '600', letterSpacing: '-0.015em' },
			h4: { fontSize: typeScale.xl, lineHeight: 1.35, fontWeight: '600', letterSpacing: '-0.01em' },
			h5: { fontSize: typeScale.lg, lineHeight: 1.4, fontWeight: '600', letterSpacing: '-0.005em' },
			h6: { fontSize: typeScale.md, lineHeight: 1.5, fontWeight: '600', letterSpacing: '0' }
		}
	},

	// Default radius for components
	radius: { xs: rem(2), sm: rem(4), md: rem(8), lg: rem(16), xl: rem(24) },

	// Spacing scale
	spacing: { xs: rem(4), sm: rem(8), md: rem(16), lg: rem(24), xl: rem(32) },

	// Shadow variations
	shadows: {
		xs: `0 ${rem(1)} ${rem(2)} rgba(16, 24, 40, 0.05)`,
		sm: `0 ${rem(1)} ${rem(3)} rgba(16, 24, 40, 0.1)`,
		md: `0 ${rem(4)} ${rem(8)} rgba(16, 24, 40, 0.12)`,
		lg: `0 ${rem(8)} ${rem(16)} rgba(16, 24, 40, 0.14)`,
		xl: `0 ${rem(12)} ${rem(24)} rgba(16, 24, 40, 0.16)`
	},

	// Component default styles
	components: {
		Button: {
			defaultProps: { size: 'md', variant: 'filled', color: 'accent' },
			styles: {
				root: {
					fontWeight: 600,
					fontSize: typeScale.sm,
					letterSpacing: '0.025em',
					textTransform: 'none',
					'&:hover': { transform: 'translateY(-1px)' }
				}
			}
		},
		ActionIcon: {
			defaultProps: { color: 'accent' }
		},
		Badge: {
			defaultProps: { color: 'accent', variant: 'filled' }
		},
		Progress: {
			defaultProps: { color: 'accent' }
		},
		Switch: {
			defaultProps: { color: 'accent' }
		},
		Loader: {
			defaultProps: { color: 'accent' }
		}
	},

	// Other theme properties
	other: {
		transition: { default: 'all 200ms ease', fast: 'all 100ms ease', slow: 'all 300ms ease' },
		// Brand gradient helpers
		gradients: {
			brand: 'linear-gradient(135deg, var(--mantine-color-accent-5), var(--mantine-color-amber-5))',
			brandSoft: 'linear-gradient(135deg, rgba(255,136,31,0.15), rgba(255,174,46,0.15))'
		}
	},
	
	// Global styles
	globalStyles: (theme) => ({
		'*, *::before, *::after': { boxSizing: 'border-box' },
		body: {
			fontSize: theme.fontSizes.md,
			lineHeight: 1.6,
			color: theme.colors.gray[8],
			fontFeatureSettings: '"cv11", "ss01", "ss03"',
			WebkitFontSmoothing: 'antialiased',
			MozOsxFontSmoothing: 'grayscale',
			textRendering: 'optimizeLegibility'
		},
		'a': {
			color: 'var(--mantine-color-accent-6)',
			textDecoration: 'none',
			transition: 'color 150ms ease',
			'&:hover': { color: 'var(--mantine-color-accent-8)', textDecoration: 'underline' }
		},
		'::selection': { backgroundColor: theme.colors.accent[1], color: theme.colors.accent[9] }
	})
});

theme.defaultProps = { colorScheme: 'light' };
theme.focusRing = 'auto';
theme.focusRingStyles = {
	styles: theme => ({ outlineOffset: rem(2), outline: `${rem(2)} solid ${theme.colors.accent[5]}` }),
	resetStyles: () => ({ outline: 'none' })
};
